// Multi-user & roles — client-side mirror of server/roles.js (docs/multi-
// user-roles-spec.md §3). UI GATING ONLY — this is convenience, never
// enforcement (spec I2): every mutating request is re-checked server-side by
// requireCapability(), so a mismatch here can only ever hide a control the
// server would still refuse, never expose one the server would allow.
//
// Keep this in lockstep with server/roles.js's MATRIX — same capabilities,
// same allowed roles per capability. If you change one, change both.
import { state } from "./state.js";

export const ROLES = ["owner", "admin", "member", "viewer"];

const MATRIX = {
  "documents.read":      ["owner", "admin", "member", "viewer"],
  "documents.write":     ["owner", "admin", "member"],
  "clients.write":       ["owner", "admin", "member"],
  "company.settings":    ["owner", "admin"],
  "stripe.connect":      ["owner", "admin"],
  "billing.view":        ["owner", "admin"],
  "billing.update_card": ["owner", "admin"],
  "billing.reenable":    ["owner", "admin"],
  "billing.cancel":      ["owner"],
  "team.read":            ["owner", "admin", "member", "viewer"],
  "team.invite":          ["owner", "admin"],
  "team.rerole":          ["owner", "admin"],
  "team.remove":          ["owner", "admin"],
  "team.leave":            ["admin", "member", "viewer"],
  "company.transfer":    ["owner"],
  "company.delete":      ["owner"]
};

// Every capability this mirror knows, so test/roles.test.mjs can hold its list
// to the server's as well as its cells: one added here alone would otherwise
// pass, because roleHasCapability answers false for a name it doesn't know.
export const CAPABILITIES = Object.freeze(Object.keys(MATRIX));

export function roleHasCapability(role, capability) {
  const allowed = MATRIX[capability];
  if (!allowed) return false;
  return allowed.includes(role);
}

// Convenience for screens: reads the role off state.membership directly, so
// call sites read as `if (can("documents.write")) { ... }` without having to
// thread the role through. A user with no membership (mid-onboarding, or a
// removed/companyless session) can do nothing.
export function can(capability) {
  const role = state.membership?.role;
  if (!role) return false;
  return roleHasCapability(role, capability);
}

// True once state.membership.role resolves to something other than owner
// with a single company — used for the "View only" topbar pill and other
// viewer-specific UI (spec §6.4).
export function isViewer() {
  return state.membership?.role === "viewer";
}

// Shared role display bits — the Team screen and the shell's company
// switcher both render role tags, so this lives in one place rather than
// two hand-maintained copies that could silently drift.
export const ROLE_LABELS = { owner: "Owner", admin: "Admin", member: "Member", viewer: "Viewer" };

export function roleLabel(role) {
  return ROLE_LABELS[role] || "Member";
}

// Owner=blue, Admin/Member=grey, Viewer=green — see design-system.html's
// Team story.
export const ROLE_PILL_CLASS = { owner: "app-pill-blue", admin: "app-pill-grey", member: "app-pill-grey", viewer: "app-pill-green" };

export function rolePillClass(role) {
  return ROLE_PILL_CLASS[role] || "app-pill-grey";
}
